import { Builder } from '@builder.io/react';
import React, { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import AudioContainer from '../../../Audio/containers/AudioContainer';
import SearchAPI from '../../../app/Search/SearchAPI';
import AudioStockItemCard from '../../../app/Search/components/card/AudioStockItemCard';
import {
  genres,
  legacyContentTypes,
  moods,
  musicInstruments,
  sfxCategories,
  vocalType as vocalTypes,
} from '../../../app/Search/entities/AudioSearchFilterOptions';
import AudioSearchOptions from '../../../app/Search/entities/AudioSearchOptions';
import { SiteEnum } from '../../../common/SiteConstants/SiteConstants';
import {
  audioSortOptions,
  defaultSortOptions,
  stockItemCardResultOptions,
} from '../../constants';
import { BottomCTABuilderInputs } from '../../constants';
import headerAlignmentFontInputs, {
  Alignment,
  FontFamily,
  HeadingLineHeight,
} from '../../utils/headerAlignmentFontInputs';
import BottomCTAButton, { BottomCTAProps } from '../elements/BottomCTAButton';
import BuilderSection from '../layouts/BuilderSection';
import AudioStockItemCardLoader from '../loading-skeletons/AudioStockItemCardLoader';

interface AudioCardListProps {
  heading: string;
  mediaType: string;
  musicGenres: Record<string, string>[];
  musicInstruments: Record<string, string>[];
  musicMoods: Record<string, string>[];
  numResults: string;
  searchTerm: string;
  sort: string;
  sfxCategories: Record<string, string>[];
  vocalType: string;
  showCallToAction: boolean;
  callToAction?: BottomCTAProps;
  headingAlignment: Alignment;
  fontFamily: FontFamily;
}

const AudioCardList = ({
  heading,
  mediaType,
  musicGenres,
  musicInstruments,
  musicMoods,
  numResults,
  searchTerm,
  sfxCategories,
  sort,
  vocalType,
  showCallToAction,
  callToAction,
  headingAlignment = Alignment.Left,
  fontFamily = FontFamily.PolySans,
}: AudioCardListProps) => {
  const [stockItems, setStockItems] = useState([]);

  const resultsToFetch = parseInt(numResults, 10);

  const getSearchResults = () => {
    setStockItems([]);

    /** NOTE: object keys should match object keys in AudioMenuFilters.js */
    const searchParameters = new AudioSearchOptions().buildSearchApiParameters({
      contentType: mediaType,
      musicMoods: musicMoods?.map(({ mood }) => mood) || [],
      musicGenres: musicGenres?.map(({ genre }) => genre) || [],
      musicInstruments: musicInstruments.map(({ instrument }) => instrument),
      resultsPerPage: resultsToFetch,
      searchOrigin: Builder.isEditing
        ? 'builder_editing'
        : 'builder_audio_card_list',
      searchTerm,
      sfxCategories: sfxCategories?.map(({ sfxCategory }) => sfxCategory) || [],
      sort,
      vocalType,
    });

    SearchAPI.searchBuilder(SiteEnum.Audioblocks, searchParameters)
      .then((res) => res.json())
      .then((response) => {
        setStockItems(response?.data?.stockItems || []);
      })
      .catch(() => {
        // [MPB] TODO: determine how to handle search loading errors.
      });
  };

  const handleFetchSearchResultsRequest = useDebouncedCallback(
    getSearchResults,
    350,
    { maxWait: 1000 }
  );

  useEffect(() => {
    handleFetchSearchResultsRequest();
  }, [
    musicGenres,
    musicInstruments,
    mediaType,
    musicMoods,
    searchTerm,
    sfxCategories,
    sort,
    vocalType,
  ]);

  return (
    <BuilderSection
      className="site-audioblocks"
      heading={heading}
      headingClassNames={`${headingAlignment} ${fontFamily} ${HeadingLineHeight} text-3xl md:text-5xl text-gray-900`}
    >
      <div className="w-full space-y-6">
        <AudioContainer />

        <div className="stock-item-group-wrapper card-design mx-0">
          {stockItems?.length < 1 && (
            <div className="flex flex-col w-full mx-0">
              {[...Array(numResults)].map((_, index) => (
                <AudioStockItemCardLoader key={`audio-card-loader-${index}`} />
              ))}
            </div>
          )}

          {stockItems?.length > 0 && (
            <div className="flex flex-col w-full mx-0">
              {stockItems.map((item, index) => (
                <AudioStockItemCard
                  {...item}
                  shouldShowAddToFavorites
                  stockItem={item.stockItem}
                  key={`audio-stock-item-card-${item.id || index}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      <BottomCTAButton show={showCallToAction} cta={callToAction} />
    </BuilderSection>
  );
};

Builder.registerComponent(AudioCardList, {
  name: 'Audio Card List',
  inputs: [
    {
      name: 'heading',
      type: 'string',
      required: true,
      defaultValue: 'Browse Music',
    },
    {
      name: 'numResults',
      friendlyName: 'Number of Results',
      type: 'enum',
      enum: stockItemCardResultOptions,
      defaultValue: '6',
      helperText:
        'Number of stock item cards to display on screen sizes >= 672 pixels (min = 3, max = 99)',
    },
    /* Audio Search Filters */
    {
      name: 'mediaType',
      type: 'string',
      required: true,
      defaultValue: 'all-audio',
      enum: legacyContentTypes,
      onChange: (options) => {
        // Clear all type-specific inputs
        options.set('musicMoods', []);
        options.set('musicGenres', []);
        options.set('musicInstruments', []);
        options.set('sfxCategories', []);
      },
    },
    {
      name: 'searchTerm',
      type: 'string',
      defaultValue: '',
    },
    {
      name: 'musicMoods',
      friendlyName: 'Moods',
      showIf: (options) => options.get('mediaType') === 'music',
      type: 'list',
      defaultValue: [],
      subFields: [
        {
          name: 'mood',
          type: 'string',
          enum: moods,
        },
      ],
    },
    {
      name: 'musicGenres',
      friendlyName: 'Genres',
      showIf: (options) => options.get('mediaType') === 'music',
      type: 'list',
      defaultValue: [],
      subFields: [
        {
          name: 'genre',
          type: 'string',
          enum: genres,
        },
      ],
    },
    {
      name: 'musicInstruments',
      friendlyName: 'Instruments',
      showIf: (options) => options.get('mediaType') === 'music',
      type: 'list',
      defaultValue: [],
      subFields: [
        {
          name: 'instrument',
          type: 'string',
          enum: musicInstruments,
        },
      ],
    },
    {
      name: 'vocalType',
      showIf: (options) => options.get('mediaType') === 'music',
      type: 'string',
      defaultValue: '',
      enum: vocalTypes,
    },
    {
      name: 'sfxCategories',
      friendlyName: 'Sound Effects Categories',
      showIf: (options) => options.get('mediaType') === 'sfx',
      type: 'list',
      defaultValue: [],
      subFields: [
        {
          name: 'sfxCategory',
          type: 'string',
          enum: sfxCategories,
        },
      ],
    },
    {
      name: 'sort',
      type: 'string',
      defaultValue: 'most_relevant',
      required: true,
      enum: [...defaultSortOptions, ...audioSortOptions],
    },
    ...headerAlignmentFontInputs,
    ...BottomCTABuilderInputs,
  ],
});
